import {
  DetailProduct,
  ProductCategories,
  ProductHighlight,
  ProductHighlightHeads,
  ProductHighlightItems,
  ProductRecommendations,
  ProductSuggestions,
  ProductSuggestionsSearchParams,
  QueryStringSearch,
} from '@diamond/shared/types';
import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError } from 'axios';
import cleanDeep from 'clean-deep';
import { query as urlQuery } from 'urlcat';

type ProductHighlightItemParams = {
  size: number;
  page: number;
  warehouse: string;
};

export const getProductList = async (
  token: string,
  params: Readonly<QueryStringSearch>
) => {
  const url = '/product';
  const { data } = await apiInstance(token).get(url, {
    params: cleanDeep(params),
  });
  return data.data;
};

type ProductCategoriesParams = {
  page: number;
  size: number;
} & Partial<{
  divisi_code: string | undefined;
  storage_code: string | undefined;
  search_query: string | undefined;
  product_id_substitute: string | null | undefined;
}>;

export const getProductCategories = async (
  token: string,
  params: ProductCategoriesParams
) => {
  const { data } = await apiInstance(token).get<ProductCategories>(
    '/product/category',
    {
      params: cleanDeep(params),
    }
  );
  return data.data;
};

export const getProductHighlightsHeads = async (
  token: string,
  warehouse: string
) => {
  try {
    const { data } = await apiInstance(token).get('/product/highlights', {
      params: { warehouse },
    });
    return data as ProductHighlightHeads;
  } catch (error) {
    if (error instanceof AxiosError) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('ProductHighlightsHeads', `list highlight heads error`);
        scope.setUser({
          ...user,
          additional: { warehouse },
        });
        scope.setLevel('error');
      });
    }
    throw new Error('Error list product highlight heads');
  }
};

export const getProductsFromHighlight = async (
  token: string,
  id: string,
  params: { size: number; page: number; warehouse: string }
) => {
  try {
    const { data } = await apiInstance(token).get(
      `/product/highlight/${id}/item`,
      { params: cleanDeep(params) }
    );
    return data as ProductHighlightItems;
  } catch (error) {
    if (error instanceof AxiosError) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('ProductsFromHighlight', `get highlight items error`);
        scope.setUser({
          ...user,
          additional: { warehouse: params.warehouse, highlight_id: id },
        });
        scope.setLevel('error');
      });
    }
    throw new Error('Error get products from highlight');
  }
};

export const getOneProductHighlight = async (
  token: string,
  id: string,
  warehouse: string
) => {
  try {
    const { data } = await apiInstance(token).get(`/product/highlight/${id}`, {
      params: { warehouse },
    });
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('OneProductHighlight', `get one product highlight error`);
        scope.setUser({
          ...user,
          additional: { warehouse, highlight_id: id },
        });
        scope.setLevel('error');
      });
    }
    throw new Error('Error get one product highlight');
  }
};

export const getProductHighlightItem = async (
  token: string,
  param: ProductHighlightItemParams
) => {
  const urlQueryParam = urlQuery(param);
  const { data } = await apiInstance(token).get(
    `/product/highlight?${urlQueryParam}`
  );
  return data;
};

export const getProductHighlightsLegacy = async (
  token: string,
  warehouse: string
): Promise<ProductHighlight> => {
  try {
    const { data } = await apiInstance(token).get('/product/highlight', {
      params: { warehouse },
    });
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag(
          'HomepageHighlightsLegacy',
          `list legacy highlights error`
        );
        scope.setUser({
          ...user,
          additional: { warehouse },
        });
        scope.setLevel('error');
      });
    }
    throw new Error('Error list product highlight (legacy)');
  }
};

export const getProductDetail = async (
  token: string,
  productId: string,
  warehouse: string
) => {
  // Return early if productId or warehouse is missing
  if (!productId || !warehouse) {
    throw new Error('Missing required parameters: productId or warehouse');
  }

  try {
    const response = await apiInstance(token).get(
      `/product/${productId}?warehouse=${warehouse}`
    );
    return response.data as DetailProduct;
  } catch (error) {
    if (error instanceof AxiosError) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('ProductDetail', `get product detail error`);
        scope.setUser({
          ...user,
          additional: {
            warehouse: warehouse,
          },
        });
        scope.setLevel('error');
      });
    }
    throw new Error('Error get product detail');
  }
};

export const getProductRecommendations = async (
  token: string,
  params: {
    search_query: string;
    warehouse: string;
  }
) => {
  try {
    const response = await apiInstance(token).get(`/product/recommendations`, {
      params,
    });
    return response.data as ProductRecommendations;
  } catch (error) {
    if (error instanceof AxiosError) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag(
          'ProductRecommendations',
          `get product recommendations error`
        );
        scope.setUser({
          ...user,
          additional: {
            warehouse: params.warehouse,
          },
        });
        scope.setLevel('error');
      });
    }
    throw new Error('Error get product recommendations');
  }
};

export const getProductSuggestions = async (
  token: string,
  id: string,
  params: ProductSuggestionsSearchParams
) => {
  try {
    const response = await apiInstance(token).get(
      `/product/${id}/substitutions`,
      {
        params: cleanDeep(params),
      }
    );
    return response.data as ProductSuggestions;
  } catch (error) {
    if (error instanceof AxiosError) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('ProductSuggestions', `get product suggestions error`);
        scope.setUser({
          ...user,
          additional: {
            warehouse: params.warehouse,
          },
        });
        scope.setLevel('error');
      });
    }
    throw new Error('Error get product suggestions');
  }
};
